import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { auth, api, fmtINR } from "@/lib/api";
import { useTickConnected } from "@/lib/ticks";
import { useInterval } from "@/lib/useInterval";
import { useLocationTracker } from "@/lib/useLocationTracker";
import {
  LineChart, User, Hourglass, ShoppingBag, PieChart, Scale, Power,
  ShieldAlert, Sun, Moon, Zap,
} from "lucide-react";
import BroadcastBar from "./BroadcastBar";
import MarketClosedBanner from "./MarketClosedBanner";
import { useTheme } from "@/lib/theme";

const navItems = [
  { to: "/dashboard",  label: "Market",   icon: LineChart, testid: "mnav-market" },
  { to: "/positions",  label: "Position", icon: User, testid: "mnav-position" },
  { to: "/orders",     label: "Pending",  icon: Hourglass, testid: "mnav-pending" },
  { to: "/tradebook",  label: "Trades",   icon: ShoppingBag, testid: "mnav-trades" },
  { to: "/ledger",     label: "Ledger",   icon: PieChart, testid: "mnav-ledger" },
  { to: "/profile",    label: "Profile",  icon: Scale, testid: "mnav-profile" },
];

const titleByPath = {
  "/dashboard": "MARKET WATCH",
  "/positions": "NET POSITION",
  "/orders":    "PENDING ORDERS",
  "/tradebook": "TRADES SUMMARY",
  "/ledger":    "LEDGER",
  "/profile":   "PROFILE",
};

function formatLimit(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)} K`;
  return n.toFixed(0);
}

export default function MobileLayout() {
  const user = auth.getUser();
  const nav = useNavigate();
  const loc = useLocation();
  const { connected } = { connected: useTickConnected() };
  const [summary, setSummary] = useState(null);
  const [feed, setFeed] = useState(null);
  const [sysSettings, setSysSettings] = useState(null);
  const { theme, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    // One-time feed status on mount
    api.get("/feed/status").then((r) => setFeed(r.data)).catch(() => {});
  }, []);

  // System settings: rarely change; WS pushes read-only events too.
  useInterval(() => {
    api.get("/system/settings").then((r) => setSysSettings(r.data)).catch(() => {});
  }, 20000);

  // Client summary — visibility-aware (pauses in background tab).
  useInterval(() => {
    api.get("/summary/me").then((r) => setSummary(r.data)).catch(() => {});
  }, 6000);

  // Session 48 (2026-06-07): Continuous location tracking while logged in.
  // Browser already has geolocation permission from login. Hook posts every
  // 60s but only when client has actually moved >50m (battery-friendly).
  useLocationTracker({ enabled: true });

  const logout = () => {
    auth.clear();
    nav("/login", { replace: true });
  };

  // Per user spec: Top P/L = MTM - Brokerage. Limit (top right) = AdminBalance + P/L.
  // Backend `/summary/me` returns:
  //   net_pl        = total_mtm - total_brokerage  (= "P/L" per user)
  //   balance_with_pl = effective limit shown on top right
  const live_pl = summary?.net_pl ?? 0;
  const limit = summary?.balance_with_pl ?? (summary?.balance ?? 0);
  const title = titleByPath[loc.pathname] || "ADITI5";
  const plPositive = live_pl >= 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--secondary))] flex justify-center overflow-x-hidden">
      {/* Mobile frame on desktop, full-width on mobile.
          pt-8 compensates for the fixed BroadcastBar (h-8). We avoid overflow-x-hidden
          on this element because that turns it into a scroll container and breaks
          sticky positioning of the navy header. */}
      <div className={`w-full max-w-[480px] min-h-screen relative bg-[hsl(var(--background))] shadow-2xl shadow-black/20 flex flex-col ${sysSettings?.read_only_mode ? "pt-[3.75rem]" : "pt-8"}`}>
        <BroadcastBar />

        {sysSettings?.read_only_mode && (
          <div className="fixed top-8 left-0 right-0 z-40 mx-auto max-w-[480px] h-7 bg-rose-100 text-destructive text-[11px] font-bold flex items-center justify-center gap-1.5"
               data-testid="read-only-banner">
            <ShieldAlert className="h-3 w-3" /> READ-ONLY MODE
          </div>
        )}

        <MarketClosedBanner />

        {/* Top dark navy header */}
        <header
          className={`sticky z-30 ${sysSettings?.read_only_mode ? "top-[3.75rem]" : "top-8"}`}
          style={{ background: "hsl(var(--header))", color: "hsl(var(--header-fg))" }}
          data-testid="mobile-topbar"
        >
          <div className="px-4 pt-3 pb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-md bg-white/10 grid place-items-center font-extrabold text-[13px]">
                A
              </div>
              <div className="leading-tight min-w-0">
                <div className="text-[15px] font-extrabold tracking-wide truncate" data-testid="mobile-page-title">
                  {title}
                </div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60">
                  <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-400 pulse-dot" : "bg-white/40"}`} />
                  {connected ? "LIVE" : "Connecting…"}
                </div>
              </div>
            </div>
            <button onClick={toggleTheme} data-testid="theme-toggle"
                    className="h-8 w-8 grid place-items-center rounded-md text-white/80 hover:bg-white/10 transition-colors"
                    title={theme === "dark" ? "Light theme" : "Dark theme"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>

          {/* P/L + Limit row */}
          <div className="px-4 pb-3 flex items-center justify-between gap-2 border-t border-white/10">
            <div className="flex items-baseline gap-1.5 pt-2.5">
              <span className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">P/L:</span>
              <span
                className={`text-[15px] font-extrabold num ${plPositive ? "text-emerald-300" : "text-rose-300"}`}
                data-testid="mobile-pl"
              >
                {fmtINR(live_pl)}
              </span>
            </div>
            <div className="flex items-baseline gap-1.5 pt-2.5">
              <span className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">Limit:</span>
              <span className="text-[15px] font-extrabold num text-white" data-testid="mobile-limit">
                {formatLimit(limit)}
              </span>
            </div>
          </div>
        </header>

        {/* Main scroll area — bottom padding leaves room for the FIXED nav (h~14) */}
        <main className="flex-1 px-3 pt-3 pb-20 w-full">
          <Outlet />
        </main>

        {/* Bottom nav — FIXED to viewport bottom inside the 480px frame so it never scrolls */}
        <nav
          className="fixed bottom-0 left-0 right-0 z-40 mx-auto max-w-[480px] grid grid-cols-7 border-t"
          style={{
            background: "hsl(var(--bottom-nav))",
            borderColor: "rgba(255,255,255,0.08)",
          }}
          data-testid="mobile-bottom-nav"
        >
          {navItems.map((it) => (
            <NavLink key={it.to} to={it.to} end data-testid={it.testid}
                     className={({ isActive }) =>
                       `flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors ${
                         isActive
                           ? "text-white bg-white/10"
                           : "text-white/55 hover:text-white/85"
                       }`
                     }>
              <it.icon className="h-5 w-5" strokeWidth={2} />
              <span className="text-[9px] uppercase tracking-wide font-bold">{it.label}</span>
            </NavLink>
          ))}
          <button onClick={logout} data-testid="mnav-logout"
                  className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-white/55 hover:text-rose-300 transition-colors">
            <Power className="h-5 w-5" strokeWidth={2} />
            <span className="text-[9px] uppercase tracking-wide font-bold">Logout</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
